import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { authService, UserProfileDto, LoginDto, RegisterDto } from '../services/authService';
import { hydrateLearnerPersianFontFromServer } from '../utils/applyLearnerPersianFont';

interface AuthContextType {
  user: UserProfileDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginDto) => Promise<void>;
  register: (userData: RegisterDto) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Fallback: redirect to login instead of crashing
    console.error('useAuth: Missing AuthProvider, redirecting to login');
    setTimeout(() => {
      // CRITICAL: Check if redirect is already in progress to prevent page freezing
      if ((window as any).__sessionRedirectInProgress) {
        return;
      }

      if (window.location.pathname !== '/login' && window.location.pathname !== '/admin/login') {
        // Set global flag to prevent other handlers from redirecting
        (window as any).__sessionRedirectInProgress = true;
        
        const isAdmin = window.location.pathname.startsWith('/admin');
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const loginPath = isAdmin ? '/admin/login' : '/login';
        const redirectUrl = `${loginPath}?reason=session_expired&returnUrl=${encodeURIComponent(currentPath)}`;
        window.location.href = redirectUrl;
      }
    }, 0);

    // Safe defaults to avoid runtime crashes while redirecting
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      login: async () => {},
      register: async () => {},
      logout: () => {},
      refreshProfile: async () => {},
    };
  }
  return context;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserProfileDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = authService.isAuthenticated() && user !== null;

  useEffect(() => {
    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          // Renew JWT first when needed — do not treat clock expiry as logout.
          if (authService.isTokenExpired()) {
            try {
              await authService.refreshToken();
            } catch {
              /* retry via getProfile / API 401 refresh */
            }
          }
          const profile = await authService.getProfile();
          setUser(profile);
          void hydrateLearnerPersianFontFromServer();
        } catch (error) {
          console.error('Failed to load user profile:', error);
          const statusCode = error && typeof error === 'object' && 'statusCode' in error
            ? (error as { statusCode?: number }).statusCode
            : undefined;
          if (statusCode === 401) {
            // Clock expiry is extended. A rejected refresh is a security failure and goes to login.
            try {
              await authService.refreshToken();
              const profile = await authService.getProfile();
              setUser(profile);
              void hydrateLearnerPersianFontFromServer();
            } catch (retryError) {
              const { learnerMayExtendSession, isSecurityRejection, redirectLearnerForSecurity } = await import('../utils/sessionExpirationPolicy');
              if (!learnerMayExtendSession() || isSecurityRejection(retryError)) {
                setUser(null);
                redirectLearnerForSecurity();
              } else {
                const cached = authService.getCurrentUser();
                if (cached) setUser(cached);
              }
            }
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();

    // Listen for logout events to clear user state
    const handleLogout = () => {
      setUser(null);
    };

    const handleLogin = (event: Event) => {
      const detail = (event as CustomEvent<{ user?: UserProfileDto | null }>).detail;
      if (detail?.user) {
        setUser(detail.user);
        return;
      }
      const cached = authService.getCurrentUser();
      if (cached) {
        setUser(cached);
      }
    };

    window.addEventListener('auth:login', handleLogin);
    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('session:expired', handleLogout);

    return () => {
      window.removeEventListener('auth:login', handleLogin);
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('session:expired', handleLogout);
    };
  }, []);

  const login = async (credentials: LoginDto) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      if (response.success && response.user) {
        setUser(response.user);
        void hydrateLearnerPersianFontFromServer();
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterDto) => {
    setIsLoading(true);
    try {
      const response = await authService.register(userData);
      if (response.success && response.user) {
        setUser(response.user);
        void hydrateLearnerPersianFontFromServer();
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const refreshProfile = async () => {
    if (authService.isAuthenticated()) {
      try {
        const profile = await authService.getProfile();
        setUser(profile);
        void hydrateLearnerPersianFontFromServer();
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};